import type { ClientSubGroupInput } from '@maru44/huntre-utils/src/models/clientSubGroup'
import { addDoc, collection, deleteDoc, doc, serverTimestamp, updateDoc } from 'firebase/firestore'
import { useCallback, useContext } from 'react'
import { ClientContext } from 'src/components/providers/ClientProvider'
import { firestore } from 'src/utils/firebase'

export const useClientSubGroups = (clientId: string) => {
  const { refreshClientSubGroups } = useContext(ClientContext)

  const createClientSubGroup = useCallback(
    async (input: ClientSubGroupInput) => {
      const ref = collection(firestore, `clients/${clientId}/clientSubGroups`)
      const now = serverTimestamp()
      await addDoc(ref, {
        name: input.name,
        clientDeviceIds: input.clientDeviceIds,
        clientMemberIds: input.clientMemberIds,
        lineTarget: input.lineTarget ?? null,
        createdAt: now,
        updatedAt: now,
      })
      await refreshClientSubGroups()
    },
    [clientId, refreshClientSubGroups],
  )

  const updateClientSubGroup = useCallback(
    async (id: string, input: ClientSubGroupInput) => {
      const ref = doc(firestore, `clients/${clientId}/clientSubGroups/${id}`)
      await updateDoc(ref, {
        name: input.name,
        clientDeviceIds: input.clientDeviceIds,
        clientMemberIds: input.clientMemberIds,
        lineTarget: input.lineTarget ?? null,
        updatedAt: serverTimestamp(),
      })
      await refreshClientSubGroups()
    },
    [clientId, refreshClientSubGroups],
  )

  const deleteClientSubGroup = useCallback(
    async (id: string) => {
      const ref = doc(firestore, `clients/${clientId}/clientSubGroups/${id}`)
      await deleteDoc(ref)
      await refreshClientSubGroups()
    },
    [clientId, refreshClientSubGroups],
  )

  const clearClientSubGroupLineTarget = useCallback(
    async (id: string) => {
      const ref = doc(firestore, `clients/${clientId}/clientSubGroups/${id}`)
      await updateDoc(ref, { lineTarget: null, updatedAt: serverTimestamp() })
      await refreshClientSubGroups()
    },
    [clientId, refreshClientSubGroups],
  )

  return { createClientSubGroup, updateClientSubGroup, deleteClientSubGroup, clearClientSubGroupLineTarget }
}
